import { ReactComponent as CrownIcon } from "@assets/images/crownSvg.svg";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import BlockIcon from "@mui/icons-material/Block";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, Grid, Stack, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import PropTypes from "prop-types";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { gradients } from "../../../../constants/background";
import { solidColors } from "../../../../constants/segmentTools";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import {
  addCustomColorNewAction,
  deleteCustomColorAction,
  editCustomColorAction,
} from "../../../../redux/actions/categoryColors";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import { colors } from "../../../../utils";
import CustomColorPicker from "../../../CustomColorPicker";
import ColorSwatch from "../../../Swatch";
import TransparentSwatch from "../../../backgroundPatterns/TransparentSwatch";
import { TRANSPARENT_PRESET_ENABLED } from "../../../../constants/featureFlags";
import Tabs from "../../../Tabs";
import { tabsVariantFor } from "../../../Tabs/tabsVariant";
import ColorPalette from "../ColorPalette";
import CustomSlider from "../Slider";
import AutoScrollAccordion from "../AutoScrollAccordion";
import * as Styled from "./ColorPopup.styled";
import WheelPaletteTab from "../../Main/WheelPaletteTab";
import FeatureEligibility from "../../../FeatureEligibility";
import { useIfUserEligible } from "../../../../hooks/useIfUserEligible";
import { FEATURES } from "../../../../constants/features";

const ColorPopup = ({
  title = "Background Fill",
  background = "#ffffff",
  handleChangeBackgroundColor,
  resetBackgroundColor,
  sliderValue,
  sliderChange,
  sliderBoxTitle,
  showSlider = true,
  sliderSymbol,
  isHideGradient = false,
  sliderMinValue = 0,
  sliderMaxValue = 100,
  showAccordion = true,
  hideDivider,
  isBorderSlider = false,
  // Gordon 2026-07-20 (REACT_APP_TRANSPARENT_PRESET): offer the shared
  // red circle-slash Transparent swatch in the SOLID preset row. Default true
  // (content-block picker); excluded surfaces (text/font colours, wheel
  // wallpaper hue, per-segment colour) pass allowTransparent={false}. Never
  // pre-selected. Inert unless the flag is ON.
  allowTransparent = true,
}) => {
  const allColorTabs = ["Preset", "Custom", "Wheel Palette", "Segment Palette"];
  const gradientColorTabs = ["Preset", "Custom"];
  const [colorsTab, setColorsTab] = useState(allColorTabs);
  const [activeColorTab, setActiveColorTab] = useState("Preset");
  const [categories, setCategories] = useState(["Solid", "Gradient"]);
  const [selectedCategory, setSelectedCategory] = useState("Solid");
  const [editColorMode, setEditColorMode] = useState(false);
  const [colorPickerVisible, setColorPickerVisible] = useState(false);
  const [temporaryColor, setTemporaryColor] = useState("#FFFFFF");
  const [selectedColorId, setSelectedColorId] = useState(null);

  const colorPickerRef = useRef(null);
  const customTabRef = useRef(null);

  useEffect(() => {
    if (selectedCategory === "Gradient") {
      setColorsTab(gradientColorTabs);
      if (!gradientColorTabs.includes(activeColorTab)) {
        setActiveColorTab(gradientColorTabs[0]);
      }
    } else {
      setColorsTab(allColorTabs);
    }
  }, [selectedCategory, activeColorTab]);

  // Add scrolling effect when the color picker opens
  useEffect(() => {
    if (colorPickerVisible && colorPickerRef.current) {
      setTimeout(() => {
        colorPickerRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [colorPickerVisible]);

  // Add scrolling effect when the Custom tab is selected
  useEffect(() => {
    if (activeColorTab === "Custom" && customTabRef.current) {
      setTimeout(() => {
        customTabRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [activeColorTab]);

  const {
    isEligible,
    subscription,
    isCheckingEligibility,
    requiredSubscription,
  } = useIfUserEligible(FEATURES.CUSTOM_HEX_COLORS);

  const myColors = useSelector((state) => state.categoryColors.myCustomColors);
  const selectedSegment = useSelector(getSelectedSegment);
  const selectedSegmentColor = selectedSegment?.settings?.color;
  const { getPaletteColor } = usePaletteColor();

  const dispatch = useDispatch();

  useEffect(() => {
    if (isHideGradient) {
      setCategories(["Solid"]);
      setSelectedCategory("Solid");
    }
  }, [isHideGradient]);

  const handleColorTabChange = (_, newValue) => {
    setActiveColorTab(newValue);
  };

  const handleCategoryChange = (_, newValue) => {
    setSelectedCategory(newValue);
  };

  const handleColorChange = (color) => {
    handleChangeBackgroundColor(color);
  };

  const handleBlockIconClick = () => {
    resetBackgroundColor();
  };

  const handleCustomColorPicker = () => {
    setEditColorMode(false);
    setColorPickerVisible(true);
  };

  const renderColorPalette = (colors) => {
    return colors.map((color) => (
      <Grid item key={color}>
        <Box
          onClick={() => {
            setEditColorMode(false);
            handleColorChange(color);
            setSelectedColorId(null);
          }}
        >
          <ColorSwatch
            color={color}
            borderColor={background === color ? "#BBBBBB" : undefined}
          />
        </Box>
      </Grid>
    ));
  };

  const renderCustomColorPalette = () => {
    return myColors.map((color) => (
      <Grid item key={color.id}>
        <Box
          onClick={() => {
            setEditColorMode(true);
            setSelectedColorId(color.id);
            handleColorChange(color.color.color_code);
          }}
        >
          <ColorSwatch
            color={color.color.color_code}
            borderColor={
              background === color.color.color_code ? "#BBBBBB" : undefined
            }
          />
        </Box>
      </Grid>
    ));
  };

  const RightIconComponent = () => (
    <div style={{ cursor: "pointer" }}>
      {background && background !== "#ffffff" ? (
        <ColorSwatch
          color={getPaletteColor(background)}
          borderColor="#79747E"
          isAccordionIcon
        />
      ) : (
        <BlockIcon />
      )}
    </div>
  );

  const handleTemporaryColorChange = (color) => {
    setTemporaryColor(color);
  };

  const onConfirmCustomColor = () => {
    const existingColor = myColors.find(
      (color) => color.color.color_code == temporaryColor
    );
    if (existingColor) {
      setSelectedColorId(existingColor?.id);
      handleColorChange(existingColor?.color?.color_code);
      setEditColorMode(true);
    } else {
      if (editColorMode) {
        dispatch(
          editCustomColorAction(selectedColorId, temporaryColor, (data) => {
            setSelectedColorId(data?.id);
            handleColorChange(data?.color?.color_code);
            setEditColorMode(true);
          })
        );
      } else {
        dispatch(
          addCustomColorNewAction(temporaryColor, (data) => {
            setSelectedColorId(data?.id);
            handleColorChange(data?.color?.color_code);
            setEditColorMode(true);
          })
        );
      }
    }

    setColorPickerVisible(false);
  };

  const handleDeleteCustomColor = () => {
    dispatch(
      deleteCustomColorAction(selectedColorId, () => {
        setSelectedColorId("");
        handleColorChange("");
        setEditColorMode(false);
      })
    );
  };

  const handleColorEditClick = () => {
    setEditColorMode(true);
    setTemporaryColor(background);
    setColorPickerVisible(true);
  };

  const content = (
    <Styled.Container>
      {showSlider && isBorderSlider && (
        <CustomSlider
          title={sliderBoxTitle}
          value={sliderValue}
          onChange={sliderChange}
          symbol={sliderSymbol}
          min={sliderMinValue}
          max={sliderMaxValue}
        />
      )}
      {!isHideGradient && (
        <Tabs
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          color="#81459B"
          indicatorColor="#C283DA"
          // Seven source tabs (Colors … Preset Wallpapers) do not fit a 432px
          // side pane at fullWidth — MUI gave each 47.1px while the labels
          // needed 55–72px, so every one clipped. Scroll them instead once the
          // strip is longer than three; shorter strips are untouched.
          variant={tabsVariantFor(categories)}
          scrollButtons="auto"
          allowScrollButtonsMobile
        />
      )}
      <Box>
        {showSlider && !isBorderSlider && (
          <CustomSlider
            title={sliderBoxTitle}
            value={sliderValue}
            onChange={sliderChange}
            symbol={sliderSymbol}
            min={sliderMinValue}
            max={sliderMaxValue}
          />
        )}

        <Grid item xs={12} style={{ marginBottom: 20 }}>
          <Tabs
            categories={colorsTab}
            selectedCategory={activeColorTab}
            onCategoryChange={handleColorTabChange}
            color="#81459B"
            indicatorColor="#C283DA"
            // Same clipping, smaller margin: "Segment Palette" needed 80px in a
            // 78px slice. The two-tab gradient variant stays fullWidth.
            // threshold=2: this strip can also land at exactly 3 tabs
            // (Segment Palette absent) with "Wheel Palette" still too wide
            // for a fullWidth three-way split (p12-wheel-palette-tab-label-
            // cut-by-9px) — see tabsVariant.js for the measurement.
            variant={tabsVariantFor(colorsTab, 2)}
            scrollButtons="auto"
            allowScrollButtonsMobile
            // vy-0914-ew-palette-source-strip-hard-clip: sibling call site
            // to SegmentColor.jsx's identical fix — scrollable alone still
            // hard-clips the tab straddling the scroller's overflow edge
            // with no fade. Same always-on both-edge fade as
            // wallpaperTabProps.js / vy-0914-ew-wallpaper-strip-left-edge-
            // hard-clip.
            sx={{
              "& .MuiTabs-scroller": {
                WebkitMaskImage:
                  "linear-gradient(to right, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%)",
                maskImage:
                  "linear-gradient(to right, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%)",
              },
            }}
          />
        </Grid>

        {activeColorTab == colorsTab[0] && (
          <Grid container mt={2} spacing={1} alignItems="center">
            {title === "Drop Shadow" && (
              <Grid item>
                <Box
                  onClick={handleBlockIconClick}
                  sx={{
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    borderRadius: "50%",
                    border: background
                      ? "none"
                      : `1px solid ${colors.lightGrey}`,
                  }}
                >
                  <BlockIcon />
                </Box>
              </Grid>
            )}
            {TRANSPARENT_PRESET_ENABLED &&
              allowTransparent &&
              selectedCategory === "Solid" && (
                <Grid item>
                  <TransparentSwatch
                    size={32}
                    selected={String(background).toLowerCase() === "transparent"}
                    onSelect={() => {
                      setEditColorMode(false);
                      setSelectedColorId(null);
                      handleColorChange("transparent");
                    }}
                  />
                </Grid>
              )}
            {renderColorPalette(
              selectedCategory === "Solid" ? solidColors : gradients
            )}
          </Grid>
        )}
        {activeColorTab === colorsTab[1] && isCheckingEligibility ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress size={40} />
          </Box>
        ) : activeColorTab === colorsTab[1] && !isEligible ? (
          <FeatureEligibility
            subscription={subscription}
            requiredSubscription={requiredSubscription}
          />
        ) : (
          activeColorTab === colorsTab[1] && (
            <Box ref={customTabRef}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mt: 2,
                  mb: 1,
                }}
              >
                <Stack
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <CrownIcon
                    style={{
                      marginRight: "10px",
                      width: "18px",
                      height: "18px",
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      fontSize: "14px",
                      fontFamily: "DM Sans",
                    }}
                  >
                    Add Custom
                  </Typography>
                </Stack>
                <Stack
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  {editColorMode && (
                    <EditOutlinedIcon
                      onClick={handleColorEditClick}
                      style={{
                        fontSize: "22px",
                        cursor: "pointer",
                        marginRight: "3px",
                        color: "#625B71",
                      }}
                    />
                  )}

                  {editColorMode && (
                    <DeleteOutlineIcon
                      onClick={handleDeleteCustomColor}
                      style={{
                        fontSize: "23px",
                        cursor: "pointer",
                        marginRight: "3px",
                        color: "#625B71",
                      }}
                    />
                  )}
                  <AddCircleOutlineOutlinedIcon
                    sx={{ color: "#625B71", cursor: "pointer" }}
                    onClick={handleCustomColorPicker}
                  />
                </Stack>
              </Box>
              <CustomColorPicker
                ref={colorPickerRef}
                customStyle={{
                  position: "fixed",
                  left: "70.5vw",
                  bottom: "14vh",
                }}
                visible={colorPickerVisible}
                onClose={() => setColorPickerVisible(false)}
                onConfirm={onConfirmCustomColor}
                color={temporaryColor}
                onColorChange={handleTemporaryColorChange}
              />
              {(!myColors.length || selectedCategory !== "Solid") && (
                <Typography
                  sx={{
                    color: "#BBBBBB",
                    mt: 1,
                    fontSize: "12px",
                    fontFamily: "DM Sans",
                  }}
                >
                  No custom colors yet — add your first one!
                </Typography>
              )}
              {selectedCategory === "Solid" && myColors.length ? (
                <Grid container spacing={1} alignItems="center">
                  {renderCustomColorPalette()}
                </Grid>
              ) : null}
            </Box>
          )
        )}
        {activeColorTab == colorsTab[2] && (
          <WheelPaletteTab
            handleSelectColor={(color) =>
              handleColorChange(`wheel_suggested_color_${color}`)
            }
            selectedColor={background}
            showButtonAndTitle={true}
          />
        )}
        {activeColorTab == colorsTab[3] && (
          <ColorPalette
            handleSelectColor={(color) =>
              handleColorChange(`suggested_color_${color}`)
            }
            selectedColor={background}
          />
        )}
      </Box>
    </Styled.Container>
  );

  if (showAccordion) {
    return (
      <AutoScrollAccordion
        title={title}
        AccordionSummaryStyle={{ p: 0, m: 0 }}
        RightIconComponent={RightIconComponent}
        hideDivider={hideDivider}
      >
        {content}
      </AutoScrollAccordion>
    );
  } else {
    return <Box>{content}</Box>;
  }
};

ColorPopup.propTypes = {
  title: PropTypes.string,
  background: PropTypes.string,
  handleChangeBackgroundColor: PropTypes.func.isRequired,
  resetBackgroundColor: PropTypes.func.isRequired,
  sliderValue: PropTypes.number,
  sliderChange: PropTypes.func.isRequired,
  sliderBoxTitle: PropTypes.string,
  showSlider: PropTypes.bool,
  sliderSymbol: PropTypes.string,
  isHideGradient: PropTypes.bool,
  sliderMinValue: PropTypes.number,
  sliderMaxValue: PropTypes.number,
  showAccordion: PropTypes.bool,
  hideDivider: PropTypes.bool,
  allowTransparent: PropTypes.bool,
};

export default ColorPopup;
